import { isImmutableString, isValidAutomergeUrl, } from "@automerge/automerge-repo/slim";
import { getType } from "./metadata.js";
const folderStrategy = {
    matches(doc) {
        return (!!doc &&
            typeof doc === "object" &&
            "docs" in doc &&
            Array.isArray(doc.docs));
    },
    async resolve(repo, handle, parts, visited) {
        const folder = handle.doc();
        if (!folder?.docs)
            return undefined;
        const part = parts[0];
        const docLink = folder.docs.find((doc) => doc.name === part);
        if (!docLink)
            return undefined;
        const next = await repo.find(docLink.url);
        return resolvePathInternal(repo, next, parts.slice(1), visited);
    },
};
const directoryStrategy = {
    matches(doc) {
        return getType(doc) === "directory";
    },
    async resolve(repo, handle, parts, visited) {
        return walkDirectoryDoc(repo, handle.doc(), parts, visited);
    },
};
async function walkDirectoryDoc(repo, node, parts, visited) {
    if (typeof node === "string" && isValidAutomergeUrl(node)) {
        // Following a url consumes no parts, so revisiting the same url with the
        // same remaining parts is a cycle.
        const key = `${node}|${parts.join("/")}`;
        if (visited.has(key))
            return undefined;
        visited.add(key);
        const next = await repo.find(node);
        return resolvePathInternal(repo, next, parts, visited);
    }
    if (parts.length === 0) {
        return materialize(repo, node, undefined, visited);
    }
    if (!node || typeof node !== "object" || node instanceof Uint8Array) {
        return undefined;
    }
    // Longest-prefix match: try "main/dist/index.js", then "main/dist", then "main"
    const obj = node;
    for (let i = parts.length; i >= 1; i--) {
        const key = parts.slice(0, i).join("/");
        if (key in obj) {
            return walkDirectoryDoc(repo, obj[key], parts.slice(i), visited);
        }
    }
    return undefined;
}
// rule of thumb: bytes pass through, anything else gets JSON.stringify'd unless
// a .mimeType hint is in scope (FileDoc-shape provides one). default mime is
// application/json so strings without a hint become valid JSON.
async function materialize(repo, node, typeHint, visited = new Set()) {
    if (typeof node === "string" && isValidAutomergeUrl(node)) {
        if (visited.has(node))
            return undefined;
        visited.add(node);
        const next = await repo.find(node);
        return materialize(repo, next.doc(), typeHint, visited);
    }
    if (node &&
        typeof node === "object" &&
        !Array.isArray(node) &&
        !(node instanceof Uint8Array) &&
        !isImmutableString(node) &&
        "content" in node) {
        const obj = node;
        return materialize(repo, obj.content, obj.mimeType ?? typeHint, visited);
    }
    if (node instanceof Uint8Array) {
        return { content: node, type: typeHint ?? "application/octet-stream" };
    }
    // String with a mime hint: pass through. Without: JSON-encode so the response
    // body matches the declared application/json type.
    if (typeof node === "string") {
        if (typeHint)
            return { content: node, type: typeHint };
        return { content: JSON.stringify(node), type: "application/json" };
    }
    if (isImmutableString(node)) {
        const s = String(node);
        if (typeHint)
            return { content: s, type: typeHint };
        return { content: JSON.stringify(s), type: "application/json" };
    }
    if (typeof node === "number" ||
        typeof node === "boolean" ||
        (node && typeof node === "object")) {
        try {
            return {
                content: JSON.stringify(node),
                type: typeHint ?? "application/json",
            };
        }
        catch {
            return undefined;
        }
    }
    return undefined;
}
const STRATEGIES = [directoryStrategy, folderStrategy];
async function resolvePathInternal(repo, handle, parts, visited) {
    if (parts.length === 0) {
        return materialize(repo, handle.doc(), undefined, visited);
    }
    const doc = handle.doc();
    for (const strategy of STRATEGIES) {
        if (strategy.matches(doc)) {
            return strategy.resolve(repo, handle, parts, visited);
        }
    }
    return undefined;
}
export async function resolvePath(repo, rootHandle, parts) {
    return resolvePathInternal(repo, rootHandle, parts, new Set());
}
